import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WA_LINK = 'https://wa.me/919990533327?text=Hi%20ZAFCO%2C%20I%20am%20interested%20in%20Studying%20in%20New%20Zealand';
const QUALS = ['12th Pass', 'Undergraduate', 'Postgraduate'];
const YEARS = ['2026', '2027', '2028'];

const validators = {
  name: (v) => v.trim().length >= 2 || 'Name is invalid. Example: Rahul Sharma',
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) || 'Email is invalid. Example: rahul@gmail.com',
  phone: (v) => /^[6-9]\d{9}$/.test(v.trim()) || 'Phone is invalid. Example: 9876543210',
  city: (v) => v.trim().length >= 2 || 'City is invalid. Example: Auckland',
};

/* 3-step interactive lead funnel modal (qualification → intake → contact) — a11y: focus trap, ESC, backdrop, focus restore */
export const NZLeadFunnelModal = ({ open, onClose, source = 'nz-hub-funnel', waLink = WA_LINK, budgets, courses }) => {
  const [step, setStep] = useState(1);
  const [qual, setQual] = useState('');
  const [intake, setIntake] = useState('');
  const [budget, setBudget] = useState('');
  const [course, setCourse] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '' });
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState('form'); // form | loading | success
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll('button, input, select, [tabindex="0"]');
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => modalRef.current?.querySelector('button, input')?.focus(), 50);
    return () => { document.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open, onClose]);

  const validateField = useCallback((k, v) => {
    const r = validators[k](v);
    setErrors((e) => ({ ...e, [k]: r === true ? '' : r }));
    return r === true;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const ok = Object.keys(validators).every((k) => validateField(k, form[k]));
    if (!ok) return;
    setPhase('loading');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'nz_lead_submit', lead_source: source, lead_qualification: qual, lead_intake: intake, lead_budget: budget, lead_course: course });
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          service_type: 'Study Abroad - New Zealand',
          country: 'New Zealand',
          qualification: qual,
          experience: '',
          source,
          message: `[NZ Lead Funnel — ${source}] Qualification: ${qual} | Target Intake: ${intake}${budget ? ` | Budget: ${budget}` : ''}${course ? ` | Course: ${course}` : ''} | Preferred City: ${form.city.trim()}`,
        }),
      });
    } catch (err) {
      // soft-fail: WhatsApp handoff must never be blocked by network errors
      console.warn('[NZLeadFunnel] lead POST failed (continuing to WhatsApp handoff):', err);
    }
    setPhase('success');
  };

  if (!open) return null;

  const field = (key, label, type, placeholder, inputMode) => (
    <div className="mb-3">
      <label htmlFor={`nz-${key}`} className="block text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">{label}</label>
      <input
        id={`nz-${key}`} type={type} inputMode={inputMode} placeholder={placeholder} value={form[key]}
        aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `nz-${key}-err` : undefined}
        onChange={(e) => { setForm((f) => ({ ...f, [key]: e.target.value })); if (errors[key]) validateField(key, e.target.value); }}
        onBlur={(e) => validateField(key, e.target.value)}
        data-testid={`nz-lead-${key}`} style={{ colorScheme: 'light' }}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A028]"
      />
      <p id={`nz-${key}-err`} aria-live="polite" className="text-xs text-red-600 mt-1 min-h-[1rem]">{errors[key] || ''}</p>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="nz-funnel-backdrop"
    >
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="New Zealand eligibility funnel" className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button aria-label="Close modal" data-testid="nz-funnel-close" onClick={onClose}
          className="absolute top-3 right-3 text-2xl leading-none text-slate-500 hover:text-slate-800 focus-visible:ring-2 focus-visible:ring-[#C5A028] rounded px-1">&times;</button>

        {phase === 'success' ? (
          <div className="text-center py-6" data-testid="nz-funnel-success">
            <p className="text-lg font-bold text-[#003087] mb-2">Application Received!</p>
            <p className="text-sm text-slate-600 mb-5">Our NZ Education Specialist will reach out on WhatsApp within 2 hours.</p>
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="nz-whatsapp-handoff"
              onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'nz_whatsapp_click', lead_source: source }); }}
              className="inline-block bg-[#25D366] text-[#0A192F] font-bold text-sm px-6 py-3 rounded-full hover:opacity-90">Chat on WhatsApp Now</a>
          </div>
        ) : step === 1 ? (
          <div>
            <h3 className="text-lg font-bold text-[#003087] mb-4">Select Your Highest Qualification</h3>
            {QUALS.map((q) => (
              <button key={q} data-testid={`nz-qual-${q.replace(/\s/g, '-').toLowerCase()}`}
                onClick={() => { setQual(q); setStep(2); }}
                className="w-full text-left border-2 border-slate-200 hover:border-[#003087] rounded-lg px-4 py-3 mb-2 text-sm font-semibold text-slate-700 focus-visible:ring-2 focus-visible:ring-[#C5A028]">{q}</button>
            ))}
          </div>
        ) : step === 2 ? (
          <div>
            <h3 className="text-lg font-bold text-[#003087] mb-4">Target Intake Year</h3>
            {YEARS.map((y) => (
              <button key={y} data-testid={`nz-intake-${y}`} onClick={() => { setIntake(y); setStep(budgets ? 3 : 4); }}
                className="w-full text-left border-2 border-slate-200 hover:border-[#003087] rounded-lg px-4 py-3 mb-2 text-sm font-semibold text-slate-700 focus-visible:ring-2 focus-visible:ring-[#C5A028]">{y}</button>
            ))}
          </div>
        ) : step === 3 && budgets ? (
          <div>
            <h3 className="text-lg font-bold text-[#003087] mb-4">Your Annual Budget (Tuition + Living)</h3>
            {budgets.map((b) => (
              <button key={b} data-testid={`nz-budget-${b.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()}`} onClick={() => { setBudget(b); setStep(4); }}
                className="w-full text-left border-2 border-slate-200 hover:border-[#003087] rounded-lg px-4 py-3 mb-2 text-sm font-semibold text-slate-700 focus-visible:ring-2 focus-visible:ring-[#C5A028]">{b}</button>
            ))}
          </div>
        ) : (
          <form action="#" onSubmit={submit} className="zaf-lead-form" noValidate>
            <h3 className="text-lg font-bold text-[#003087] mb-4">Almost There — Your Details</h3>
            {field('name', 'Name', 'text', 'Full name')}
            {field('email', 'Email Address', 'email', 'you@email.com')}
            {field('phone', 'WhatsApp Number', 'tel', '10-digit mobile', 'numeric')}
            {field('city', 'Preferred City', 'text', 'e.g., Auckland')}
            {courses && (
              <div className="mb-3">
                <label htmlFor="nz-course" className="block text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">Preferred Course</label>
                <select id="nz-course" value={course} onChange={(e) => setCourse(e.target.value)} data-testid="nz-lead-course"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A028]">
                  <option value="">Select course field (optional)</option>
                  {courses.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={phase === 'loading'} data-testid="nz-lead-submit"
              className="w-full bg-[#003087] hover:bg-[#0056B3] text-white font-bold text-sm px-6 py-3 rounded-full disabled:opacity-60">
              {phase === 'loading' ? 'Submitting…' : 'Get My Free NZ Assessment'}
            </button>
            <span className="zaf-micro-trust block text-center text-[11px] text-slate-500 mt-2">⚡ Takes 60 seconds • 100% Free • No Spam Guarantee</span>
          </form>
        )}
      </div>
    </div>
  );
};
